import Text from 'antd/lib/typography/Text';

import Drawer from 'antd/lib/drawer';
import * as React from 'react';
import {
    FeatureFlag,
    FeatureFlagAudience,
    FeatureFlagRule as FeatureFlagRuleType,
    FeatureFlagStatus,
    FeatureFlagVariation as FeatureFlagVariationType,
    FeatureFlagVariationVariable,
    IntegrationType,
} from '../../types';
import Layout, { Content } from 'antd/lib/layout/layout';
import {
    buildAudiencePredicateText,
    buildConditionValueText,
    buildPredicateText,
    ToggleFeatureFlagRequest,
    tryUpdateFeatureFlags,
} from '../../actions/featureFlags';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import { useDispatch } from 'react-redux';
import Switch from 'antd/lib/switch';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import { FeatureFlagStatusTag } from './FeatureFlagStatus';
import { FeatureFlagStar } from './FeatureFlags';
import Button from 'antd/lib/button';
import Descriptions from 'antd/lib/descriptions';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import { ArrowRightOutlined } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import { FeatureFlagUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import Link from 'antd/lib/typography/Link';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

export interface FeatureFlagDrawerProps extends ReleaseComponentProps {
    featureFlag?: FeatureFlag;
    open: boolean;
    onClose: () => void;
    currentVersion: string;
    integrationIcon: string;
    integrationIconAlt: string;
    integrationId: IntegrationType;
    integrationName: string;
    projectName: string;
    environmentName: string;
}

export const FeatureFlagDrawer: React.FC<FeatureFlagDrawerProps> = (props) => {
    const toggleFeatureFlagError = useError(FeatureFlagUpdatable.ToggleFeatureFlag, props.featureFlag?.id);
    const toggleFeatureFlagSuccess = useUpdated(FeatureFlagUpdatable.ToggleFeatureFlag, props.featureFlag?.id);

    React.useEffect(() => {
        if (toggleFeatureFlagError && props.open) {
            globalMessage.error(toggleFeatureFlagError);
        }
    }, [toggleFeatureFlagError]);

    React.useEffect(() => {
        if (toggleFeatureFlagSuccess && props.open) {
            globalMessage.success('Feature flag toggled successfully.');
        }
    }, [toggleFeatureFlagSuccess]);

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Space size={8}>
                        {props.integrationIcon && (
                            <img src={props.integrationIcon} alt={props.integrationIconAlt} width={21} height={21} />
                        )}
                        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Feature flag detail</Text>
                    </Space>
                    {props.featureFlag && <FeatureFlagStatusTag fill status={props.featureFlag.status} />}
                </div>
            }
            headerStyle={{ backgroundColor: antGrey.grey2 }}
            placement="right"
            closable={false}
            destroyOnClose
            onClose={props.onClose}
            open={props.open}
            width={473}
        >
            {props.featureFlag && <FeatureFlagDetails {...props} />}
        </Drawer>
    );
};

export default FeatureFlagDrawer;

const FeatureFlagDetails: React.FC<FeatureFlagDrawerProps> = (props) => {
    return (
        <Layout style={{ marginBottom: 60 }}>
            <Content>
                <FeatureFlagHeader {...props} />
                <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                <FeatureFlagKeyInfo {...props} />
                {props.featureFlag.rules && props.featureFlag.rules.length > 0 && (
                    <>
                        <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                        <SectionHeader title="Rules" />
                        {props.featureFlag.rules.map((rule) => (
                            <FeatureFlagRule key={rule.id} rule={rule} />
                        ))}
                    </>
                )}
                {props.featureFlag.variations.length > 0 && (
                    <>
                        <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                        <SectionHeader title="Variations" />
                        {props.featureFlag.variations.map((variation) => {
                            return (
                                <>
                                    <FeatureFlagVariation
                                        key={variation.name}
                                        variation={variation}
                                        integrationType={props.integrationId}
                                        integrationName={props.integrationName}
                                        rules={props.featureFlag.rules.filter((rule) => {
                                            const variationIds = rule.variations.map((v) => v.key);
                                            return variationIds.includes(variation.key);
                                        })}
                                    />
                                </>
                            );
                        })}
                    </>
                )}
            </Content>
        </Layout>
    );
};

const FeatureFlagHeader: React.FC<FeatureFlagDrawerProps> = (props) => {
    const { featureFlag, projectName, environmentName } = props;
    const dispatch = useDispatch();
    const toggleFeatureFlagIsLoading = useIsLoading(FeatureFlagUpdatable.ToggleFeatureFlag, props.featureFlag.id);

    const toggleFlag = (flagId: string, enabled: boolean) => {
        const request: ToggleFeatureFlagRequest = {
            featureFlagId: flagId,
            enable: enabled,
            releaseId: props.releaseId,
            appId: props.appId,
            orgId: props.orgId,
        };
        dispatch(tryUpdateFeatureFlags(FeatureFlagUpdatable.ToggleFeatureFlag, request, flagId));
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space size={8}>
                    <Text style={{ fontWeight: 400, color: antGrey.grey8, lineHeight: '22px', fontSize: 14 }}>
                        <Text style={{ color: runwayColors.primary }}>{projectName}</Text>
                        <Text type="secondary" style={{ color: antGrey.grey7 }}>
                            {' '}
                            - {environmentName}
                        </Text>
                    </Text>
                </Space>
                <Switch
                    loading={toggleFeatureFlagIsLoading}
                    onChange={(checked) => {
                        toggleFlag(featureFlag.id, checked);
                    }}
                    disabled={
                        ![FeatureFlagStatus.Enabled, FeatureFlagStatus.Disabled].includes(featureFlag.status) ||
                        featureFlag.isReadOnly
                    }
                    defaultChecked={featureFlag.status === FeatureFlagStatus.Enabled}
                />
            </div>
            <Space size={12} style={{ marginTop: 10, marginBottom: 36 }}>
                {featureFlag.shouldHighlight && <FeatureFlagStar tooltipText={featureFlag.highlightTooltipText} />}
                <Text style={{ fontSize: 20, lineHeight: '28px' }}>{featureFlag.name}</Text>
            </Space>
            {featureFlag.url && (
                <Button
                    icon={<ArrowRightOutlined style={{ color: runwayColors.primary }} />}
                    href={featureFlag.url}
                    target="_blank"
                    style={{ display: 'block', width: '50%' }}
                >
                    {`Open in ${props.integrationName}`}
                </Button>
            )}
        </>
    );
};

const FeatureFlagKeyInfo: React.FC<FeatureFlagDrawerProps> = (props) => {
    return (
        <Descriptions colon={false} column={1} contentStyle={{ width: '50%', justifyContent: 'right' }}>
            <Descriptions.Item label="Minimum version">
                <FeatureFlagVersionItem value={props.featureFlag.minimumVersion} />
            </Descriptions.Item>
            <Descriptions.Item label="Maximum version">
                <FeatureFlagVersionItem value={props.featureFlag.maximumVersion} />
            </Descriptions.Item>
            <Descriptions.Item label="Specific target versions">
                <FeatureFlagVersionItem
                    value={
                        props.featureFlag.equalVersions?.length > 0 ?
                            props.featureFlag.equalVersions.join(', ')
                        :   undefined
                    }
                />
            </Descriptions.Item>
            {props.featureFlag.enabledAt && (
                <Descriptions.Item label="Enabled on">
                    {renderEnabledAt(
                        props.featureFlag.enabledAt,
                        props.featureFlag.isEstimatedEnabledAt,
                        props.integrationName,
                    )}
                </Descriptions.Item>
            )}
            {props.featureFlag.estimatedDisabledAt && (
                <Descriptions.Item label="Disabled on">
                    {renderDisabledAt(props.featureFlag.estimatedDisabledAt)}
                </Descriptions.Item>
            )}
            {props.featureFlag.owner && (
                <Descriptions.Item label="Owner">
                    <Text>{props.featureFlag.owner}</Text>
                </Descriptions.Item>
            )}
            <Descriptions.Item label="Created on">
                <Text>{dayjs(props.featureFlag.createdAt).format(DatetimeFormat.ShortMonthDateYear)}</Text>
            </Descriptions.Item>
        </Descriptions>
    );
};

const FeatureFlagVersionItem: React.FC<{ value?: string }> = ({ value }) => (
    <Text ellipsis style={value ? { textAlign: 'right', fontWeight: 600 } : null}>
        {value ?? '-'}
    </Text>
);

const SectionHeader: React.FC<{ title: string }> = (props) => {
    return (
        <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 16 }}>
            <Text style={{ fontSize: 12, color: antGrey.grey7, letterSpacing: '0.1em' }}>
                {props.title.toUpperCase()}
            </Text>
        </div>
    );
};

const FeatureFlagVariation: React.FC<{
    variation: FeatureFlagVariationType;
    integrationType: IntegrationType;
    integrationName: string;
    rules: FeatureFlagRuleType[];
}> = (props) => {
    const renderVariablesListItems = (variables: FeatureFlagVariationVariable[]) => {
        return variables.map((variable) => (
            <div key={variable.key}>
                <Text code>{variable.key}</Text>
                <Text>
                    {' '}
                    ({variable.type}), value: {variable.value}
                </Text>
            </div>
        ));
    };
    const rulesContent =
        props.rules.length === 0 ? null
        : props.rules.length === 1 ?
            <Text style={{ textAlign: 'right' }}>
                used in <strong>{` ${props.rules[0].name}`}</strong>
            </Text>
        :   <Text style={{ textAlign: 'right' }}>
                used in rules <strong>{` ${props.rules.map((r) => r.name).join(', ')}`}</strong>
            </Text>;
    return (
        <Space size={6} direction="vertical" style={cardStyle}>
            <Descriptions colon={false} column={1} contentStyle={{ justifyContent: 'right' }}>
                <Descriptions.Item
                    label={
                        <Text
                            style={{ fontSize: 16, maxWidth: '150px', fontWeight: 600, color: antGrey.grey8 }}
                        >{`${props.variation.name}`}</Text>
                    }
                >
                    {rulesContent}
                </Descriptions.Item>
                {props.variation.variables && props.variation.variables.length > 0 && (
                    <Descriptions.Item label="Variables">
                        <Space align="end" direction="vertical">
                            {renderVariablesListItems(props.variation.variables)}
                        </Space>
                    </Descriptions.Item>
                )}
            </Descriptions>
        </Space>
    );
};

const renderEnabledAt = (enabledAt: string, wasEstimated: boolean, integrationName: string) => {
    const textComponent = <Text>{dayjs(enabledAt).format(DatetimeFormat.ShortMonthDateYear)}</Text>;
    if (wasEstimated) {
        return (
            <Tooltip
                title={`The ${integrationName} API doesn’t provide a timestamp for when flags are enabled. Because of this, this date is an estimate and might not reflect the exact date that this feature flag was enabled.`}
            >
                {textComponent}
            </Tooltip>
        );
    } else {
        return textComponent;
    }
};

const renderDisabledAt = (disabledAt: string) => {
    const textComponent = <Text>{dayjs(disabledAt).format(DatetimeFormat.ShortMonthDateYear)}</Text>;
    return (
        <Tooltip
            title={`This value is an estimate and might not reflect the exact date that this feature flag was disabled.`}
        >
            {textComponent}
        </Tooltip>
    );
};

const FeatureFlagRule: React.FC<{
    rule: FeatureFlagRuleType;
}> = (props) => {
    const { rule } = props;
    const variationsText = `${rule.variations
        .map((v) => `${v.weight < 100 ? `${v.weight}% ` : ''}${v.name ?? v.key}`)
        .join(', ')}`;

    const audienceItems = rule.audienceConditions?.map((condition, index) => (
        <div key={index}>
            <Text style={{ textAlign: 'right' }}>
                {`${condition.weight}% of ${buildAudiencePredicateText(condition.predicate, condition.negate)} `}
                {condition.audiences?.map((audience, index, array) => (
                    <>
                        {renderAudienceText(audience)}
                        {index < array.length - 1 ? ', ' : ''}
                    </>
                ))}
            </Text>
            {rule.audienceConditions.length > 1 && index < rule.audienceConditions.length - 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Text>AND</Text>
                </div>
            )}
        </div>
    ));

    const conditionItems = rule.conditions?.map((condition, index) => (
        <div key={index}>
            <Text code>{condition.attribute}</Text>
            <Text>
                {' '}
                {buildPredicateText(condition.predicate, condition.negate)} {buildConditionValueText(condition)}
            </Text>
        </div>
    ));

    return (
        <Space size={6} direction="vertical" style={cardStyle}>
            <Text style={{ fontSize: 16, fontWeight: 600, color: antGrey.grey8 }}>{` ${rule.name}`}</Text>
            <div>
                <Descriptions
                    colon={false}
                    column={1}
                    contentStyle={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'right',
                        alignItems: 'flex-end',
                    }}
                >
                    {rule.audienceConditions && <Descriptions.Item label="Serve to">{audienceItems}</Descriptions.Item>}
                    {rule.conditions && (
                        <Descriptions.Item label="Conditions">
                            <Space direction="vertical" align="end">
                                {conditionItems}
                            </Space>
                        </Descriptions.Item>
                    )}
                </Descriptions>
                <Divider style={{ marginLeft: 2, marginRight: 2, marginTop: 2, marginBottom: 7 }} />
                <Descriptions colon={false} column={1} contentStyle={{ justifyContent: 'right' }}>
                    <Descriptions.Item label={`Variation${rule.variations.length != 1 ? 's' : ''}`}>
                        <Text style={{ textAlign: 'right' }}>{variationsText}</Text>
                    </Descriptions.Item>
                </Descriptions>
            </div>
        </Space>
    );
};

const renderAudienceText = (audience: FeatureFlagAudience) => {
    if (audience.url) {
        return (
            <Link href={audience.url} target="_blank">
                {audience.name}
            </Link>
        );
    } else {
        return <Text>{audience.name}</Text>;
    }
};

const cardStyle = {
    background: antGrey.grey2,
    borderRadius: 8,
    paddingBottom: 8,
    paddingLeft: 18,
    paddingRight: 18,
    paddingTop: 14,
    marginBottom: 12,
};
